// Data freshness and time handling for Security Operations (/soc).
//
// A tile's lastSyncAt is the time its area last received data: Defender XDR's last successful sync
// (incidents, data), the connector's last scan (network, external threat), or the newest update of
// the area's controls (SIEM, cloud, identity). Email, cloud apps and detection rules send their
// OLDEST source's time. The page judges an area by its NEWEST connector instead (see areaLastSync):
// an area is stale only when every connector has stopped, and a connector that stopped on its own is
// named on the tile and in the banner rather than marking the area's current figures as old. Every source is collected at most once a day (Defender
// XDR daily at 03:00 UTC; Fortinet, surface, secrets and brand scans daily), so an area is stale once
// it has gone longer than one missed run plus margin. The wording is "no new data since", never
// "sync failed": the page knows when data last arrived, not why it stopped.

export const STALE_AFTER_HOURS = {
  incidents: 48,
  identity: 48,
  endpoint: 48,
  cloud: 48,
  siem: 48,
  network: 48,
  external: 48,
  data: 48,
  email: 48,
  // AI discovery scans every 24 h by default.
  agenticAi: 48,
  cloudApps: 48,
  detectionRules: 48,
};

const HOUR_MS = 60 * 60 * 1000;
const HAS_ZONE = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

// The API serializes UTC times; one without a zone suffix is still UTC (a value read back from SQL
// Server carries no kind). new Date() would read it as local time, so the suffix is added first.
export const parseUtc = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = String(value).trim();
  const date = new Date(HAS_ZONE.test(text) ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isConnected = (tile) => !!tile && tile.status !== "notConnected" && tile.connected !== false;

// state: "notConnected" | "unknown" (connected, no sync time) | "stale" | "fresh". Ages are computed on
// epoch milliseconds, so time zones and daylight-saving changes never shift them.
// When the area last received data: its newest connector's time when its connectors report times,
// else the tile's own time.
export const areaLastSync = (tile) => {
  const times = (tile?.sources || []).map((s) => parseUtc(s?.lastSyncAt)).filter(Boolean);
  if (times.length > 0) return new Date(Math.max(...times.map((t) => t.getTime())));
  return parseUtc(tile?.lastSyncAt);
};

export const freshnessOf = (tile, now = new Date()) => {
  if (!isConnected(tile)) return { state: "notConnected", lastSync: null };
  const lastSync = areaLastSync(tile);
  if (!lastSync) return { state: "unknown", lastSync: null };
  const ageMs = Math.max(0, now.getTime() - lastSync.getTime());
  const limit = STALE_AFTER_HOURS[tile.key];
  const stale = limit != null && ageMs > limit * HOUR_MS;
  return { state: stale ? "stale" : "fresh", lastSync, ageMs };
};

// The connectors behind a tile (its `sources`, each with its own lastSyncAt) that have sent nothing for
// longer than the tile's limit, oldest first, so a stale area can name what stopped. Empty when the
// tile lists no sources or none of them is past the limit.
export const staleSources = (tile, now = new Date()) => {
  const limit = STALE_AFTER_HOURS[tile?.key];
  if (limit == null) return [];
  return (tile.sources || [])
    .map((s) => ({ name: s?.name, lastSync: parseUtc(s?.lastSyncAt) }))
    .filter((s) => s.name && s.lastSync)
    .map((s) => ({ ...s, ageMs: Math.max(0, now.getTime() - s.lastSync.getTime()) }))
    .filter((s) => s.ageMs > limit * HOUR_MS)
    .sort((a, b) => b.ageMs - a.ageMs);
};

// "just now", "12 min", "5 h", "3 days".
export const formatAge = (ms) => {
  if (ms == null || Number.isNaN(ms)) return "";
  const minutes = Math.floor(Math.max(0, ms) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"}`;
};

// Local date and time for display.
export const formatLocal = (date) => (date ? date.toLocaleString() : "");
export const formatLocalDate = (date) => (date ? date.toLocaleDateString() : "");
